import {
  Box,
  Button,
  Card,
  CircularProgress,
  Collapse,
  Fade,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Image from "../../common/Image";
import { rule5AppUrl, rule5properties } from "../../properties";
import { StyledTextfield } from "../../common/StyledComponents";
import ClearIcon from "@mui/icons-material/Clear";
import { useDialog } from "../../context/DialogContext";
import ResearchCompany from "../../modal/ResearchCompany";
import { throttle } from "lodash";
import {
  createUrlSearchParams,
  useDisplayContext,
} from "../../context/DisplayContext";
import useOutreach from "../../integrations/useOutreach";

/* Landing page that automatically searches and loads opportunities for provided company name. */
export default function SFLanding(props) {
  let params = new URLSearchParams(document.location.search);
  // If redirectType is unspecified, it will default to the company research. either research or gpt.
  const redirectType = params.get("redirect");

  const displayContext = useDisplayContext();

  const [statusMessage, setStatusMessage] = React.useState(
    "Continue typing to search companies."
  );
  const [loading, setLoading] = React.useState(true);
  const [accountList, setAccountList] = React.useState([]);
  const [selectedAccount, setSelectedAccount] = React.useState();
  const [selectedAccountOpps, setSelectedAccountOpps] = React.useState([]);
  const [searchValue, setSearchValue] = React.useState();

  const dialog = useDialog();
  const { company: outreachCompany } = useOutreach();
  const sfCompany = params.get("companyName");
  const defaultCompany = sfCompany ? { name: sfCompany } : outreachCompany;

  useEffect(() => {
    if (outreachCompany) {
      setSelectedAccount(outreachCompany);
    }
  }, [outreachCompany]);

  // Throttling function for company search.
  const fetchCompanies = React.useMemo(
    () =>
      throttle((input, callback) => {
        axios
          .get(
            `${rule5properties.matchCompanyBySfdcCompanyName}?sfdcAccountName=${input}`
          )
          .then((res) => {
            if (res.data?.id) {
              callback([res.data]);
            } else {
              // Fallback to company search if sf company match not found
              axios
                .get(`${rule5properties.companies}?name=${input}`)
                .then((res) => {
                  if (res.data) {
                    callback(res.data);
                  } else {
                  }
                });
            }
          });
      }, 200),
    []
  );

  // When the search value is changed, call the API to search for companies.
  useEffect(() => {
    const name = sfCompany || searchValue;

    setSelectedAccount(null);
    setSelectedAccountOpps([]);

    if (name?.length > 2) {
      setLoading(true);
      // Search the endpoint for accounts that match the provided account name.
      // If there's multiple accounts that match, display list of results for user to select.
      fetchCompanies(encodeURIComponent(name), (result) => {
        setLoading(false);
        if (Array.isArray(result)) {
          let accountList = result;
          setAccountList(accountList);
          if (accountList.length === 0) {
            // No account match, allow user to request research on this company.
            setStatusMessage(`No results found for "${name}".`);
          }
          if (accountList.length === 1) {
            // Automatically select the one account in the list.
            setSelectedAccount(accountList[0]);
          }
          if (accountList.length > 1) {
            // If more than one account is in the list, first search the list to see if there are any exact matches.
            let exactMatch = accountList.find(
              (account) =>
                account.name.trim().toLowerCase() === name?.trim().toLowerCase()
            );
            if (exactMatch) {
              setSelectedAccount(exactMatch);
            } else {
              setStatusMessage(
                `There were multiple accounts found for "${name}". Specify a company to research.`
              );
            }
          }
          setLoading(false);
        } else {
          setLoading(false);
        }
      });
    } else {
      setStatusMessage("Continue typing to search companies.");
      setLoading(false);
      setAccountList([]);
    }
  }, [sfCompany, searchValue, fetchCompanies]);

  // Selected account was changed, call endpoint that returns list of researches for the selected account.
  useEffect(() => {
    if (selectedAccount) {
      setLoading(true);
      axios
        .get(
          rule5properties.getOpportunityList +
            `?companyId=${selectedAccount?.id}`
        )
        .then((res) => {
          if (res.data && Array.isArray(res.data)) {
            setSelectedAccountOpps(res.data);
          } else {
            console.log(
              "Issue fetching opportunities for company: " +
                selectedAccount?.name
            );
          }
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
        });
    } else {
      setSelectedAccountOpps([]);
    }
  }, [selectedAccount, accountList]);

  // After the account was selected, the opportunities were loaded for the account and displayed.
  useEffect(() => {
    // If we are in research mode, we can perform a redirect to the URL parameter specified opportunity.
    if (redirectType !== "gpt") {
      // If the selected account only has one opportunity, redirect to that opportunity.
      if (selectedAccountOpps.length === 1) {
        window.location.replace(
          `${rule5AppUrl}/main/opportunities/${
            selectedAccountOpps[0]?.id
          }/research?${createUrlSearchParams(
            selectedAccountOpps[0],
            selectedAccount?.id
          )}`
        );
      }
      // See if there is an opportunity that matches the provided functional area.
      let matchedOpp = selectedAccountOpps.find(
        (el) =>
          el.functionalArea.toLowerCase() ===
          params.get("functionalArea")?.toLowerCase()
      );
      if (matchedOpp) {
        window.location.replace(
          `${rule5AppUrl}/main/opportunities/${
            matchedOpp.id
          }/research?${createUrlSearchParams(matchedOpp, selectedAccount.id)}`
        );
      }
    }
  }, [selectedAccountOpps, params]);

  return (
    <Stack alignItems="center">
      <Stack sx={{ p: 3, maxWidth: "600px", gap: 2 }}>
        <StyledTextfield
          value={sfCompany || searchValue || defaultCompany?.name}
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
          disabled={defaultCompany}
          endAdornment={
            <InputAdornment>
              <IconButton size="small" disabled={defaultCompany}>
                <ClearIcon
                  sx={{ width: "18px", height: "18px" }}
                  onClick={() => {
                    setSearchValue("");
                  }}
                />
              </IconButton>
            </InputAdornment>
          }
        />
        {!selectedAccount ? (
          <Box>
            <Typography sx={{ opacity: 0.8 }}>{statusMessage}</Typography>
            <Box>
              <Collapse
                in={loading}
                unmountOnExit
                sx={{ alignItems: "center" }}
              >
                <Stack alignItems="center">
                  <CircularProgress sx={{ my: 3 }} />
                </Stack>
              </Collapse>
              {accountList?.map((account, index) => (
                <AccountListItem
                  account={account}
                  key={index}
                  clickFunction={() => {
                    setSelectedAccount(account);
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box>
            <Stack direction="row" alignItems="center">
              {!defaultCompany && (
                <Tooltip title="Return to list">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedAccount(null);
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Typography sx={{ mt: "2px", fontSize: "16px" }}>
                Accounts for {selectedAccount?.name}
                {selectedAccountOpps.length === 0 && !loading && (
                  <Typography variant="settingsCaption" sx={{ mt: "15px" }}>
                    None found.
                  </Typography>
                )}
              </Typography>
            </Stack>
            <Stack>
              <Collapse
                in={loading}
                unmountOnExit
                sx={{ alignItems: "center" }}
              >
                <Stack alignItems="center">
                  <CircularProgress />
                </Stack>
              </Collapse>
              {selectedAccountOpps.map((opp, index) => (
                <OpportunityListItem
                  opp={opp}
                  account={selectedAccount}
                  key={index}
                  redirectType={redirectType}
                />
              ))}
            </Stack>
          </Box>
        )}
        {(defaultCompany?.name || searchValue?.trim().length > 2) &&
          !loading && (
            <Stack alignItems="center" sx={{ marginTop: "20px" }}>
              <Typography sx={{ opacity: 0.7, fontSize: "14px" }}>
                Couldn't find the exact company research you were looking for?
                You can request a company's research using the button below.
              </Typography>
              <Button
                disableElevation
                variant="contained"
                sx={{
                  width: "200px",
                  textTransform: "none",
                  mt: 2,
                }}
                onClick={(event) =>
                  dialog.openModal(
                    "Research Company",
                    ResearchCompany,
                    { defaultCompany: selectedAccount },
                    "sm"
                  )
                }
              >
                Request research
              </Button>
            </Stack>
          )}
      </Stack>
    </Stack>
  );
}

/* Component for list item displaying an account */
function AccountListItem(props) {
  return (
    <Card
      variant="outlined"
      sx={{ p: 2, borderRadius: "6px", mt: 1, cursor: "pointer" }}
      onClick={props.clickFunction}
    >
      <Stack direction="row" alignItems="center" sx={{ height: "36px" }}>
        {props.account.companyInfo.icon ? (
          <Image
            style={{ maxHeight: "30px", maxWidth: "30px" }}
            src={props.account.companyInfo.icon}
          />
        ) : (
          <img
            loading="lazy"
            width="30"
            src={`https://logo.clearbit.com/${props.account.domain}?s=128`}
            alt=""
          />
        )}
        <Box sx={{ ml: 3 }}>
          <Typography>{props.account.name}</Typography>
        </Box>
      </Stack>
    </Card>
  );
}

/* Component for list item displaying an opportunity */
function OpportunityListItem(props) {
  let { opp, redirectType, account } = props;
  return (
    <Card
      variant="outlined"
      sx={{ p: 2, borderRadius: "6px", mt: 1, cursor: "pointer" }}
      onClick={
        redirectType !== "gpt"
          ? () => {
              window.location.replace(
                `${rule5AppUrl}/main/opportunities/${
                  opp.id
                }/research?${createUrlSearchParams(opp, account.id)}`
              );
            }
          : () => {
              // Redirect type is GPT.
              window.location.replace(
                `${rule5AppUrl}/main/chat?${createUrlSearchParams(
                  opp,
                  account.id
                )}&filterToDefault=true`
              );
            }
      }
    >
      {opp?.functionalArea}
    </Card>
  );
}
